import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";
import { themeQuartz } from "ag-grid-community";
import images from "../../../assets/images";
import { PageLoader } from "../../../components/loader/Loader";
import { ClipboardCheck } from "lucide-react";
import {
  useSafetyFacilityRoundsStore,
  ChecklistTemplate,
} from "../../../store/SafetyFacilityRoundsStore";
import { toast } from "react-toastify";
import {
  ColDef,
  ICellRendererParams,
  ValueGetterParams,
} from "ag-grid-community";

const gridTheme = themeQuartz.withParams({
  backgroundColor: "#fff",
  foregroundColor: "#2a2a2a",
  headerTextColor: "#2a2a2a",
  headerBackgroundColor: "#E9F5FF",
  oddRowBackgroundColor: "rgb(0, 0, 0, 0.03)",
  headerColumnResizeHandleColor: "rgb(126, 46, 132)",
  fontSize: "0.75rem",
  headerFontWeight: "bold",
});

const ChecklistList: React.FC = () => {
  const navigate = useNavigate();
  const {
    checklists,
    loading,
    error,
    fetchChecklists,
    deleteChecklist,
    categories,
  } = useSafetyFacilityRoundsStore();

  const [rowData, setRowData] = useState<ChecklistTemplate[]>([]);
  const [originalRowData, setOriginalRowData] = useState<ChecklistTemplate[]>(
    [],
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [isActiveFilter, setIsActiveFilter] = useState<boolean | undefined>(
    undefined,
  );

  useEffect(() => {
    fetchChecklists({
      category: selectedCategory || undefined,
      isActive: isActiveFilter,
    });
  }, [selectedCategory, isActiveFilter, fetchChecklists]);

  useEffect(() => {
    if (checklists.length > 0) {
      setOriginalRowData(checklists);
      setRowData(checklists);
    }
  }, [checklists]);

  useEffect(() => {
    if (searchTerm.trim()) {
      const filtered = originalRowData.filter(
        (checklist) =>
          checklist.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          checklist.checklistCode
            .toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          checklist.category.toLowerCase().includes(searchTerm.toLowerCase()),
      );
      setRowData(filtered);
    } else {
      setRowData(originalRowData);
    }
  }, [searchTerm, originalRowData]);

  const handleDelete = async (id: string, name: string) => {
    if (
      window.confirm(
        `Are you sure you want to delete the checklist "${name}"? This action cannot be undone.`,
      )
    ) {
      try {
        await deleteChecklist(id);
        toast.success("Checklist deleted successfully");
        fetchChecklists({
          category: selectedCategory || undefined,
          isActive: isActiveFilter,
        });
      } catch (error: any) {
        toast.error(error.message || "Failed to delete checklist");
      }
    }
  };

  const handleNavigate = (id: string, mode: "view" | "edit") => {
    navigate(`/safety-facility-rounds/checklists/${mode}/${id}`);
  };

  const columnDefs = useMemo<ColDef<ChecklistTemplate>[]>(
    () => [
      {
        field: "checklistCode",
        headerName: "Checklist Code",
        flex: 1,
        minWidth: 150,
        headerStyle: { backgroundColor: "#e9f5ff" },
        cellStyle: { padding: "10px 0 0 15px" },
      } as ColDef<ChecklistTemplate>,
      {
        field: "name",
        headerName: "Name",
        flex: 1,
        minWidth: 200,
        headerStyle: { backgroundColor: "#e9f5ff" },
        cellStyle: { padding: "10px 0 0 15px" },
      } as ColDef<ChecklistTemplate>,
      {
        field: "category",
        headerName: "Category",
        flex: 1,
        minWidth: 150,
        headerStyle: { backgroundColor: "#e9f5ff" },
        cellStyle: { padding: "10px 0 0 15px" },
        cellRenderer: (params: ICellRendererParams<ChecklistTemplate>) => {
          const category = params.value;
          return (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
              {category}
            </span>
          );
        },
      } as ColDef<ChecklistTemplate>,
      {
        field: "items",
        headerName: "Items",
        flex: 1,
        minWidth: 100,
        headerStyle: { backgroundColor: "#e9f5ff" },
        cellStyle: { padding: "10px 0 0 15px" },
        valueGetter: (params: ValueGetterParams<ChecklistTemplate>) =>
          params.data?.items?.length || 0,
      } as ColDef<ChecklistTemplate>,
      {
        field: "isActive",
        headerName: "Status",
        flex: 1,
        minWidth: 120,
        headerStyle: { backgroundColor: "#e9f5ff" },
        cellStyle: { padding: "10px 0 0 15px" },
        cellRenderer: (params: ICellRendererParams<ChecklistTemplate>) => {
          const isActive = params.value;
          return (
            <span
              className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${isActive
                  ? "bg-green-100 text-green-800"
                  : "bg-gray-100 text-gray-800"
                }`}
            >
              {isActive ? "Active" : "Inactive"}
            </span>
          );
        },
      } as ColDef<ChecklistTemplate>,
      {
        field: "createdAt",
        headerName: "Created",
        flex: 1,
        minWidth: 150,
        headerStyle: { backgroundColor: "#e9f5ff" },
        cellStyle: { padding: "10px 0 0 15px" },
        valueGetter: (params: ValueGetterParams<ChecklistTemplate>) =>
          params.data?.createdAt
            ? new Date(params.data.createdAt).toLocaleDateString("en-US", {
              year: "numeric",
              month: "short",
              day: "numeric",
            })
            : "-",
      } as ColDef<ChecklistTemplate>,
      {
        headerName: "Actions",
        flex: 1,
        minWidth: 120,
        headerStyle: { backgroundColor: "#e9f5ff" },
        cellStyle: { padding: "10px 0 0 15px" },
        cellRenderer: (params: ICellRendererParams<ChecklistTemplate>) => {
          const data = params.data;
          if (!data || !data._id) return null;

          return (
            <div className="flex gap-x-3">
              <button
                onClick={() => handleNavigate(data._id!, "view")}
                className="p-1.5 hover:bg-blue-50 text-blue-600 rounded-lg transition-colors"
                title="View Checklist"
              >
                <img className="w-5 h-5" src={images.viewIcon} alt="View" />
              </button>
              <button
                onClick={() => handleNavigate(data._id!, "edit")}
                className="p-1.5 hover:bg-amber-50 text-amber-600 rounded-lg transition-colors"
                title="Edit Checklist"
              >
                <img className="w-5 h-5" src={images.editIcon} alt="Edit" />
              </button>
              <button
                onClick={() =>
                  navigate(
                    `/safety-facility-rounds/checklists/perform/${data._id}`,
                  )
                }
                className="p-1.5 hover:bg-green-50 text-green-600 rounded-lg transition-colors"
                title="Perform Round"
              >
                <ClipboardCheck size={18} />
              </button>
            </div>
          );
        },
      } as ColDef<ChecklistTemplate>,
    ],
    [navigate],
  );

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      resizable: true,
    }),
    [],
  );

  const theme = useMemo(() => {
    return gridTheme;
  }, []);

  if (loading && checklists.length === 0) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <PageLoader />
      </div>
    );
  }

  return (
    <div className="px-2 py-4 mt-14  min-h-100vh">
      <div className="flex justify-between items-center mb-4">
        <h2 className="mb-3">Safety & Facility Rounds Checklists</h2>
        <button
          onClick={() => navigate("/safety-facility-rounds/checklists/create")}
          className="flex items-center justify-center font-semibold gap-2 bg-[#0fa9a6] text-white px-4 py-2 rounded-full"
        >
          CREATE CHECKLIST
        </button>
      </div>

      <div className="tableBlock w-full">
        <div className="card">
          <div className="tableSearchBlock flex justify-between items-center">
            <div className="leftTableSearchBlock">
              <div className="input-affix">
                <i className="prefix-icon anticon anticon-search"></i>
                <input
                  type="text"
                  className="form-control"
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search Here..."
                />
              </div>
            </div>

            <span className="flex gap-8">
              <select
                className="form-control"
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                style={{ minWidth: "200px" }}
              >
                <option value="">All Categories</option>
                {categories.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>
              <select
                className="form-control"
                value={
                  isActiveFilter === undefined
                    ? ""
                    : isActiveFilter
                      ? "active"
                      : "inactive"
                }
                onChange={(e) => {
                  const value = e.target.value;
                  setIsActiveFilter(
                    value === ""
                      ? undefined
                      : value === "active"
                        ? true
                        : false,
                  );
                }}
                style={{ minWidth: "150px" }}
              >
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </span>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded m-4">
              {error}
            </div>
          )}

          {loading ? (
            <div className="flex justify-center items-center h-64">
              <PageLoader />
            </div>
          ) : rowData.length > 0 ? (
            <div className="ag-theme-alpine w-full border-0 rounded-lg shadow-lg">
              <AgGridReact
                rowData={rowData}
                columnDefs={columnDefs}
                theme={theme}
                headerHeight={65}
                rowHeight={50}
                defaultColDef={defaultColDef}
                pagination={true}
                paginationPageSize={10}
                domLayout="autoHeight"
              />
            </div>
          ) : (
            <div className="flex flex-col justify-center items-center h-64">
              <p className="text-gray-500 text-lg mb-4">No checklists found</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChecklistList;
